import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Eye, CalendarDays, MessageSquare, Heart, Upload, Download, Trash2, Check, Power, Crown, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/store";
import { PlanCard } from "@/components/PlanCard";
import { PromoteButtons } from "@/components/PromoteButtons";

const STAT = [
  { key: "views", label: "stat_views", icon: Eye, color: "#002FA7" },
  { key: "chats", label: "stat_chats", icon: MessageSquare, color: "#0A0B10" },
  { key: "bookings", label: "stat_bookings", icon: CalendarDays, color: "#155724" },
  { key: "saves", label: "stat_saves", icon: Heart, color: "#FF3B30" },
];

const TEMPLATE = "title,description,category,price_per_day,price_per_week,currency,country,city,postal_code,payment_terms,image_url,website,whatsapp,instagram,facebook,tiktok\n" +
  "Excavator 5t,Compact excavator for site work,tools,180,950,AUD,AU,Sydney,2000,Bank transfer or card on pickup; A$500 bond,https://images.unsplash.com/photo-1581094794329-c8112a89af12,https://mysite.com,+61400000000,@myrentals,,\n";

const fmt = (s, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{${k}}`, v), s);

export default function OwnerDashboard() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [listings, setListings] = useState([]);
  const [prices, setPrices] = useState({});
  const [importing, setImporting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [resub, setResub] = useState(null);
  const [rf, setRf] = useState({ title: "", description: "", note: "" });
  const resubmit = async (id) => {
    try { await api.post(`/listings/${id}/resubmit`, rf); toast.success(t("resubmit_sent")); setResub(null); loadListings(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const fileRef = useRef(null);

  const loadPlan = () => api.get("/plan").then(({ data }) => setPlan(data)).catch(() => {});
  const setShowcase = async (id) => {
    try { await api.put("/plan/showcase", { listing_id: id }); toast.success(t("showcase_set")); loadPlan(); }
    catch (e) { toast.error(e.response?.status === 402 ? t("showcase_pro_only") : formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadStats = () => api.get("/my/stats").then(({ data }) => setData(data)).catch(() => setData({ listings: [], totals: {} }));
  const loadListings = () => api.get("/my/listings").then(({ data }) => {
    setListings(data);
    setPrices(Object.fromEntries(data.map((l) => [l.listing_id, l.price_per_day])));
  }).catch(() => {});
  useEffect(() => { loadStats(); loadListings(); loadPlan(); }, []);

  const savePrice = async (id) => {
    try { await api.patch(`/listings/${id}`, { price_per_day: parseFloat(prices[id]) }); toast.success(t("price_updated")); loadListings(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (l) => {
    try { await api.patch(`/listings/${l.listing_id}`, { active: !l.active }); toast.success(l.active ? t("paused") : t("active")); loadListings(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/listings/${id}`); toast.success(t("listing_deleted")); loadListings(); loadStats(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/listings/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(fmt(t("imported"), { n: data.created }));
      if (data.errors?.length) toast.error(`${fmt(t("rows_skipped"), { n: data.errors.length })}: ${data.errors.slice(0, 2).join("; ")}`);
      loadListings(); loadStats();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "rentglobe-listings-template.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  if (!data) return <div className="py-24 text-center text-gray-400">…</div>;
  const maxViews = Math.max(1, ...data.listings.map((l) => l.views));

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight" data-testid="dash-title"><BarChart3 className="text-[#002FA7]" /> {t("dash_title")}</h1>
      <p className="mt-2 text-gray-500">{t("dash_sub")}</p>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="dashboard-totals">
        {STAT.map((s) => (
          <div key={s.key} className="rounded-2xl border border-gray-200 p-5">
            <s.icon size={18} style={{ color: s.color }} />
            <div className="mt-3 font-display text-3xl font-extrabold text-gray-900">{data.totals[s.key] || 0}</div>
            <div className="text-xs font-bold uppercase tracking-widest text-gray-400">{t(s.label)}</div>
          </div>
        ))}
      </div>

      <PlanCard />

      <div className="mt-10 rounded-2xl border border-gray-200 p-5" data-testid="bulk-import">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-display text-lg font-bold"><Upload size={18} className="text-[#002FA7]" /> {t("import_title")}</div>
            <p className="mt-1 text-sm text-gray-500">{t("import_sub")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="download-template" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700 hover:border-[#002FA7] hover:text-[#002FA7]"><Download size={15} /> {t("template")}</button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="csv-input" />
            <button data-testid="import-btn" disabled={importing} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-full bg-[#002FA7] px-5 py-2 text-sm font-bold text-white disabled:opacity-60"><Upload size={15} /> {importing ? t("importing") : t("upload_csv")}</button>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold">{t("manage_listings")} ({listings.length})</h2>
        <div className="flex flex-wrap gap-2">
          {plan && !plan.is_pro && listings.length > 0 && (
            <a href="#plan-card" data-testid="dash-upgrade-pro-cta" className="inline-flex items-center gap-1.5 rounded-full bg-[#002FA7] px-4 py-1.5 text-sm font-bold text-white transition-colors hover:bg-[#002280]"><Crown size={15} className="text-yellow-300" /> {t("plan_upgrade")} — A${plan.prices?.AUD}{t("per_month")}</a>
          )}
          <Link to="/create" data-testid="add-single" className="rounded-full border border-[#002FA7] px-4 py-1.5 text-sm font-bold text-[#002FA7] hover:bg-[#002FA7] hover:text-white">{t("add_one")}</Link>
        </div>
      </div>
      {listings.length === 0 ? (
        <p className="mt-4 text-gray-400" data-testid="manage-empty">{t("no_listings")}</p>
      ) : (
        <div className="mt-4 space-y-2" data-testid="manage-listings">
          {listings.map((l) => (
            <div key={l.listing_id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 p-3" data-testid={`manage-${l.listing_id}`}>
              {l.image_url && <img src={l.image_url} alt="" className="h-12 w-12 rounded-lg object-cover" />}
              <div className="min-w-[140px] flex-1">
                <Link to={`/listing/${l.listing_id}`} className="font-bold text-gray-900 hover:text-[#002FA7] line-clamp-1">{l.title}</Link>
                <div className="text-xs text-gray-400">{l.category} · {l.city || l.country} · {l.active ? t("active") : t("paused")}</div>
                {l.moderation?.removed && l.moderation.status !== "pending_review" && <div data-testid={`moderation-notice-${l.listing_id}`} className="mt-1 inline-flex items-center gap-1 rounded-md bg-[#FF3B30]/10 px-2 py-0.5 text-xs font-semibold text-[#FF3B30]"><AlertTriangle size={12} /> {t("removed_by_moderation")}: {l.moderation.reason}</div>}
                {l.moderation?.status === "pending_review" && <div data-testid={`pending-review-${l.listing_id}`} className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"><AlertTriangle size={12} /> {t("pending_review")}</div>}
                {l.moderation?.removed && l.moderation.status !== "pending_review" && (
                  resub === l.listing_id ? (
                    <div className="mt-2 space-y-2 rounded-xl border border-gray-200 p-3" data-testid={`resubmit-form-${l.listing_id}`}>
                      <input data-testid={`resubmit-title-${l.listing_id}`} value={rf.title} onChange={(e) => setRf({ ...rf, title: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
                      <textarea data-testid={`resubmit-desc-${l.listing_id}`} rows={2} value={rf.description} onChange={(e) => setRf({ ...rf, description: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
                      <textarea data-testid={`resubmit-note-${l.listing_id}`} rows={2} value={rf.note} onChange={(e) => setRf({ ...rf, note: e.target.value })} placeholder={t("resubmit_note")} className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
                      <div className="flex gap-2">
                        <button data-testid={`resubmit-send-${l.listing_id}`} onClick={() => resubmit(l.listing_id)} className="rounded-full bg-[#002FA7] px-4 py-1.5 text-xs font-bold text-white">{t("resubmit_send")}</button>
                        <button onClick={() => setResub(null)} className="rounded-full border border-gray-300 px-4 py-1.5 text-xs font-bold text-gray-600">{t("cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    <button data-testid={`resubmit-${l.listing_id}`} onClick={() => { setResub(l.listing_id); setRf({ title: l.title, description: l.description || "", note: "" }); }} className="mt-1 ml-2 rounded-full border border-[#002FA7] px-3 py-0.5 text-xs font-bold text-[#002FA7] hover:bg-[#002FA7] hover:text-white">{t("resubmit")}</button>
                  )
                )}
              </div>
              <div className="flex items-center gap-1">
                <input type="number" min="0" step="0.01" value={prices[l.listing_id] ?? ""} onChange={(e) => setPrices((p) => ({ ...p, [l.listing_id]: e.target.value }))}
                  data-testid={`price-${l.listing_id}`} className="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#002FA7]" />
                <span className="text-xs text-gray-400">{l.currency}/d</span>
                <button data-testid={`save-price-${l.listing_id}`} onClick={() => savePrice(l.listing_id)} className="grid h-8 w-8 place-items-center rounded-full bg-[#155724] text-white" title={t("save_price")}><Check size={15} /></button>
              </div>
              <button data-testid={`toggle-${l.listing_id}`} onClick={() => toggleActive(l)} className={`grid h-8 w-8 place-items-center rounded-full border ${l.active ? "border-gray-300 text-gray-500" : "border-[#155724] text-[#155724]"}`} title={l.active ? t("pause") : t("publish")}><Power size={15} /></button>
              {plan?.is_pro && (plan.plan?.showcase_listing_id === l.listing_id
                ? <span data-testid={`showcase-active-${l.listing_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#0A0B10] px-3 py-1.5 text-xs font-bold text-yellow-300"><Crown size={13} /> {t("showcase_active")}</span>
                : <button data-testid={`showcase-${l.listing_id}`} onClick={() => setShowcase(l.listing_id)} className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-600 hover:border-[#0A0B10] hover:text-[#0A0B10]" title={t("set_showcase")}><Crown size={13} /> {t("set_showcase")}</button>)}
              <button data-testid={`delete-${l.listing_id}`} onClick={() => del(l.listing_id)} className="grid h-8 w-8 place-items-center rounded-full border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/5" title={t("delete")}><Trash2 size={15} /></button>
              {l.active && !l.moderation?.removed && (
                <div className="basis-full border-t border-gray-100 pt-2" data-testid={`promote-row-${l.listing_id}`}>
                  {plan?.is_pro ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#155724]"><Crown size={13} /> {t("plan_perk2")}</span>
                  ) : (
                    <PromoteButtons listing={l} label={t("promote_this_listing")} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-4 mt-10 font-display text-xl font-bold">{t("perf_title")}</h2>
      {data.listings.length === 0 ? (
        <p className="text-gray-400" data-testid="dashboard-empty">{t("no_perf")}</p>
      ) : (
        <div className="space-y-3" data-testid="dashboard-listings">
          {data.listings.map((l) => (
            <div key={l.listing_id} className="rounded-2xl border border-gray-200 p-4" data-testid={`dash-${l.listing_id}`}>
              <div className="flex items-center gap-3">
                {l.image_url && <img src={l.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                <Link to={`/listing/${l.listing_id}`} className="flex-1 font-bold text-gray-900 hover:text-[#002FA7] line-clamp-1">{l.title}</Link>
                <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{l.views} {t("views_lc")}</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-[#002FA7]" style={{ width: `${(l.views / maxViews) * 100}%` }} /></div>
              </div>
              <div className="mt-2 text-xs text-gray-500">{fmt(t("perf_line"), { chats: l.chats, bookings: l.bookings, saves: l.saves })}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
